import React, { useState, useEffect, useRef } from "react";
import { useLocation, useNavigate, useSearchParams } from "react-router-dom";
import { useDispatch, useSelector } from "react-redux";
import {
  CreditText,
  DropdownCredits,
  DropdownFooter,
  DropdownItem,
  DropdownItemBadge,
  DropdownItemLabel,
  DropdownMenu,
  DropdownNav,
  DropdownSeparator,
  DropdownUserInfo,
  DropdownUsername,
  DropdownUserSection,
  HamburgerIcon,
  HeaderContainer,
  HeaderRightSide,
  HeaderWrapper,
  LogoImg,
  LogoTagline,
  LogoWrapper,
  MenuButton,
  ProUpgradeButtonContainer,
  ProUpgradeButtonSkeleton,
} from "./Header.styles";
import { logout } from "../../store/slices/auth/asyncThunks";
import { selectLoggedIn } from "../../store/slices/auth/slice";
import { selectUser } from "../../store/slices/user/slice";
import {
  selectCreditsHistory,
  selectCreditsHistoryFetched,
} from "../../store/slices/subscription/slice";
import { getSubscriptionsCreditsHistory } from "../../store/slices/subscription/asyncThunks";
import {
  selectAutoPlay,
  selectSignUpModalOpen,
  setAffiliateRef,
  setAutoPlay,
  setSignUpModalOpen,
} from "../../store/slices/common/slice";
import { FiEdit3, FiLogOut, FiUsers, FiPlay, FiPause } from "react-icons/fi";
import { HiOutlineSparkles } from "react-icons/hi2";
import { LuSun, LuMoon } from "react-icons/lu";
import AuthModal from "../AuthModal/AuthModal";
import FreeCreditsModal from "../FreeCreditsModal/FreeCreditsModal";
import TopUpModal from "../TopUpModal/TopUpModal";
import UsernameModal from "../UsernameModal/UsernameModal";
import { priceFormatter } from "../../utils/utils";
import { toast } from "sonner";
import { TbCreditCardPay } from "react-icons/tb";
import { useTheme } from "../../contexts/ThemeContext";
import DropIcon from "../DropIcon/DropIcon";

const Header = () => {
  const dispatch = useDispatch();
  const [searchParams, setSearchParams] = useSearchParams();
  const [isDropdownOpen, setIsDropdownOpen] = useState(false);
  const [isDropdownAnimating, setIsDropdownAnimating] = useState(false);
  const [isLoginModalOpen, setIsLoginModalOpen] = useState(false);
  const [isUsernameModalOpen, setIsUsernameModalOpen] = useState(false);
  const [isFreeCreditsModalOpen, setIsFreeCreditsModalOpen] = useState(false);
  const [isTopUpOpen, setIsTopUpOpen] = useState(false);
  const navigate = useNavigate();
  const location = useLocation();
  const loggedIn = useSelector(selectLoggedIn);
  const user = useSelector(selectUser);
  const creditsHistory = useSelector(selectCreditsHistory);
  const creditsHistoryFetched = useSelector(selectCreditsHistoryFetched);
  const dropdownRef = useRef(null);
  const signUpModalOpen = useSelector(selectSignUpModalOpen);
  const autoplayEnabled = useSelector(selectAutoPlay);
  const [isScrolled, setIsScrolled] = useState(false);
  const [isHeaderHidden, setIsHeaderHidden] = useState(false);
  const { theme, toggleTheme } = useTheme();

  const handleAutoplayToggle = () => {
    localStorage.setItem("autoPlay", !autoplayEnabled);
    dispatch(setAutoPlay(!autoplayEnabled));
  };

  const closeDropdown = () => {
    setIsDropdownAnimating(true);
    setTimeout(() => {
      setIsDropdownOpen(false);
      setIsDropdownAnimating(false);
    }, 150);
  };

  useEffect(() => {
    if (user) {
      if (user.username.includes("-")) {
        setIsUsernameModalOpen(true);
      }
    }
  }, [user]);

  /* Pre-load credits history so the navbar can decide between routing
     to /subscription (has activity/balance) vs. opening the TopUp modal
     directly (brand-new account with nothing to show). Header mounts
     per-route via MainContainer, so we gate on the Redux `fetched` flag
     to avoid refetching on every page navigation — flag resets on
     LOGOUT via the slice's initialState. */
  useEffect(() => {
    if (loggedIn && !creditsHistoryFetched) {
      dispatch(getSubscriptionsCreditsHistory(1))
        .unwrap()
        .catch((err) => console.log(err.message));
    }
  }, [loggedIn, creditsHistoryFetched, dispatch]);

  useEffect(() => {
    const TOP_THRESHOLD = 30;
    const DELTA = 3;
    let lastY = window.scrollY;
    let ticking = false;

    const onScroll = () => {
      if (ticking) return;
      ticking = true;
      window.requestAnimationFrame(() => {
        ticking = false;
        // While a modal locks body scroll (useLockBodyScroll pins body to
        // position:fixed), the browser fires synthetic scroll events as
        // scrollY snaps to 0 on open and back on close. Ignore those —
        // they aren't real user scrolling, so the header state must not
        // change just because a modal opened or closed.
        if (document.body.style.position === "fixed") return;

        const currentY = window.scrollY;
        setIsScrolled(currentY > TOP_THRESHOLD);

        // Visible only at the very top of the page or when scrolling up.
        // Hidden in every other case.
        if (currentY <= TOP_THRESHOLD || currentY < lastY - DELTA) {
          setIsHeaderHidden(false);
        } else if (currentY > lastY + DELTA) {
          setIsHeaderHidden(true);
        }

        lastY = currentY;
      });
    };

    onScroll();
    window.addEventListener("scroll", onScroll, { passive: true });
    return () => window.removeEventListener("scroll", onScroll);
  }, []);

  useEffect(() => {
    if (signUpModalOpen) {
      setIsLoginModalOpen(true);
    }
  }, [signUpModalOpen]);

  useEffect(() => {
    if (searchParams.get("code")) {
      setIsLoginModalOpen(true);
    }
    const ref = searchParams.get("ref");

    const savedAffiliateRef = localStorage.getItem("affiliateRef");
    const savedDateAffiliateRef = localStorage.getItem("dateAffiliateRef");
    let hasStoredRef = false;

    if (
      savedAffiliateRef &&
      savedDateAffiliateRef &&
      new Date(savedDateAffiliateRef).getTime() >
        new Date().getTime() - 2 * 24 * 60 * 60 * 1000
    ) {
      dispatch(setAffiliateRef(savedAffiliateRef));
      hasStoredRef = true;
    }

    if (ref) {
      if (loggedIn) {
        searchParams.delete("ref");
        setSearchParams(searchParams);
      } else {
        if (!hasStoredRef) {
          localStorage.setItem("affiliateRef", ref);
          localStorage.setItem("dateAffiliateRef", new Date().toISOString());
          dispatch(setAffiliateRef(ref));
        }
      }
    }
  }, [searchParams, dispatch, loggedIn, setSearchParams]);

  useEffect(() => {
    const handleClickOutside = (event) => {
      if (dropdownRef.current && !dropdownRef.current.contains(event.target)) {
        closeDropdown();
      }
    };

    if (isDropdownOpen) {
      document.addEventListener("mousedown", handleClickOutside);
    }

    return () => {
      document.removeEventListener("mousedown", handleClickOutside);
    };
  }, [isDropdownOpen]);

  const toggleDropdown = () => {
    if (isDropdownOpen) {
      closeDropdown();
    } else {
      setIsDropdownOpen(true);
    }
  };

  const handleNavigate = (path) => {
    navigate(`/${path}`);
    setTimeout(() => {
      window.scrollTo({
        top: 0,
        left: 0,
        behavior: "instant",
      });
    }, 300);
  };

  const hasRealActivity = (creditsHistory?.data ?? []).some(
    (item) => item?.type !== "FREE",
  );
  const isAccountEmpty =
    loggedIn && !creditsHistory?.loading && !hasRealActivity;

  const openSubscriptionOrTopUp = () => {
    if (isAccountEmpty) {
      setIsTopUpOpen(true);
    } else {
      handleNavigate("subscription");
    }
  };

  const handleUpgradeToPro = () => {
    if (loggedIn) {
      openSubscriptionOrTopUp();
    } else {
      setIsLoginModalOpen(!isLoginModalOpen);
    }
  };

  const handleDropdownItemClick = (action) => {
    if (action === "autoPlay") {
      handleAutoplayToggle();
    } else if (action === "theme") {
      toggleTheme();
      return;
    } else if (action === "subscriptions") {
      openSubscriptionOrTopUp();
    } else if (action === "freeCredits") {
      setIsFreeCreditsModalOpen(true);
    } else if (action === "deleteAccount") {
      handleNavigate("account/delete");
    } else if (action === "logout") {
      toast.success("Successfully logged out!");
      dispatch(logout());
    } else if (action === "affiliate") {
      handleNavigate("referral/earnings");
    }
    closeDropdown();
  };

  const handleFreeCreditsModalClose = () => {
    setIsFreeCreditsModalOpen(false);
  };

  const handleLogoClick = () => {
    if (location.pathname === "/") return;
    navigate("/");
    window.scrollTo({
      top: 0,
      left: 0,
      behavior: "instant",
    });
  };

  return (
    <HeaderContainer $offScreen={isHeaderHidden}>
      <HeaderWrapper $hidden={!isScrolled}>
        {/* Left: round menu button (acts as profile/dropdown trigger) */}
        <div ref={dropdownRef} style={{ position: "relative" }}>
          <MenuButton
            onClick={toggleDropdown}
            aria-label="Menu"
            aria-expanded={isDropdownOpen}
          >
            <HamburgerIcon $open={isDropdownOpen}>
              <span />
              <span />
              <span />
            </HamburgerIcon>
          </MenuButton>

          {isDropdownOpen && (
            <DropdownMenu
              $animating={isDropdownAnimating}
              style={{ left: 0, right: "auto" }}
            >
              {loggedIn && user ? (
                <>
                  <DropdownUserSection>
                    <DropdownUserInfo>
                      <DropdownUsername>
                        {user?.username}
                        <FiEdit3
                          size={13}
                          style={{
                            cursor: "pointer",
                            marginLeft: 6,
                            opacity: 0.5,
                            verticalAlign: "middle",
                          }}
                          onClick={() => setIsUsernameModalOpen(true)}
                        />
                      </DropdownUsername>
                      <DropdownCredits>
                        <DropIcon aria-hidden="true" size={16} />
                        {user !== undefined
                          ? priceFormatter(user.credits)
                          : 0}{" "}
                        drops
                      </DropdownCredits>
                    </DropdownUserInfo>
                  </DropdownUserSection>

                  <DropdownNav>
                    <DropdownItem
                      onClick={() => handleDropdownItemClick("freeCredits")}
                    >
                      <HiOutlineSparkles />
                      <DropdownItemLabel>Free Drops</DropdownItemLabel>
                      <DropdownItemBadge $glow>Daily</DropdownItemBadge>
                    </DropdownItem>
                    <DropdownItem
                      onClick={() => handleDropdownItemClick("subscriptions")}
                    >
                      <TbCreditCardPay />
                      <DropdownItemLabel>Subscription</DropdownItemLabel>
                    </DropdownItem>
                    <DropdownItem
                      onClick={() => handleDropdownItemClick("affiliate")}
                    >
                      <FiUsers />
                      <DropdownItemLabel>Affiliate</DropdownItemLabel>
                    </DropdownItem>
                  </DropdownNav>

                  <DropdownSeparator />
                </>
              ) : null}

              <DropdownFooter>
                <DropdownItem
                  onClick={() => handleDropdownItemClick("theme")}
                  aria-label="Toggle dark mode"
                >
                  {theme === "dark" ? <LuSun /> : <LuMoon />}
                  <DropdownItemLabel>
                    {theme === "dark" ? "Light mode" : "Dark mode"}
                  </DropdownItemLabel>
                </DropdownItem>

                <DropdownItem
                  onClick={() => handleDropdownItemClick("autoPlay")}
                >
                  {autoplayEnabled ? <FiPlay /> : <FiPause />}
                  <DropdownItemLabel>AutoPlay</DropdownItemLabel>
                </DropdownItem>

                {loggedIn ? (
                  <DropdownItem
                    onClick={() => handleDropdownItemClick("logout")}
                  >
                    <FiLogOut />
                    <DropdownItemLabel>Logout</DropdownItemLabel>
                  </DropdownItem>
                ) : (
                  <DropdownItem
                    onClick={() => {
                      closeDropdown();
                      setIsLoginModalOpen(true);
                    }}
                  >
                    <FiLogOut style={{ transform: "rotate(180deg)" }} />
                    <DropdownItemLabel>Log in</DropdownItemLabel>
                  </DropdownItem>
                )}
              </DropdownFooter>
            </DropdownMenu>
          )}
        </div>

        {/* Center: wordmark logo */}
        <LogoWrapper onClick={handleLogoClick}>
          <LogoImg src="/logos/fapify.webp" alt="Fapify" />
          <LogoTagline>#1 ADULT VIDEO GENERATOR</LogoTagline>
        </LogoWrapper>

        {/* Right: Drops pill */}
        <HeaderRightSide>
          {user !== null ? (
            <ProUpgradeButtonContainer onClick={handleUpgradeToPro}>
              <DropIcon aria-hidden="true" size={16} />
              <CreditText>
                {user !== undefined ? priceFormatter(user.credits) : 0}
              </CreditText>
            </ProUpgradeButtonContainer>
          ) : (
            <ProUpgradeButtonSkeleton $animationTime={5} $width={66} />
          )}
        </HeaderRightSide>

        <FreeCreditsModal
          isOpen={isFreeCreditsModalOpen}
          onClose={handleFreeCreditsModalClose}
        />

        <AuthModal
          isOpen={isLoginModalOpen}
          onClose={() => {
            dispatch(setSignUpModalOpen(false));
            setIsLoginModalOpen(false);
          }}
        />

        <UsernameModal
          isOpen={isUsernameModalOpen}
          onClose={() => setIsUsernameModalOpen(false)}
        />

        <TopUpModal
          isOpen={isTopUpOpen}
          onClose={() => setIsTopUpOpen(false)}
        />
      </HeaderWrapper>
    </HeaderContainer>
  );
};

export default Header;
